import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_USERS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Colleague } from '@/types/domain'

/* Who may be named the Operations owner when a quote becomes a case (S-1).
 *
 * GET /api/users/operations-owners, guarded by `quote.send` — the people who
 * convert are the people who pick. The answer is the `/users` Colleague shape
 * (id, name, role, office) narrowed by the SERVER to accounts that can sign in,
 * hold `case.operations.lead`, and reach cases at `assigned` or `all`. That
 * rule reads permissions this client sees for nobody but itself, so nothing is
 * filtered here: a copy of it would drift, and the conversion re-checks the
 * pick inside its transaction regardless (422 `operationsOwnerId`).
 *
 * `role` is the role KEY; a picker names it with `useRoleLabel`, because a
 * custom dispatcher role holding the key is exactly who this list exists for.
 */
export function useOperationsOwners(
  /** Fetched only while the conversion sheet is open. */
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery<Colleague[]>({
    queryKey: queryKeys.operationsOwners(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_OPERATIONS_OWNERS
        : (await api.get<{ items: Colleague[] }>('/users/operations-owners')).items,
    /* No staleTime, unlike the roster's five minutes: this list answers a
       refusable question — may this person own the case NOW — and an answer
       minutes old is how a picker offers somebody deactivated meanwhile. One
       read per opened sheet is cheap. */
  })
}

/* The fixture answer: the active seeded Operations accounts, projected from the
   user fixtures rather than written out again. */
const MOCK_OPERATIONS_OWNERS: Colleague[] = MOCK_USERS.filter(
  (user) => user.isActive && user.role === 'operations',
).map((user) => ({
  id: user.id,
  name: user.name,
  role: user.role,
  office: user.office,
}))
